import { uid } from "./utils";

/**
 * 汎用アコーディオン
 *
 * 開閉そのものはネイティブ <details> / <summary> に任せる。
 * （クリック・Enter・Space・排他グループ <details name="..."> は全てブラウザの機能）
 *
 * このモジュールが担当するのは以下だけ。
 *   1. 高さアニメーション用のカスタムプロパティ --details-height の実測
 *   2. aria-expanded / aria-controls の同期
 *   3. 中身のサイズ変化（画像読み込み・モーダル表示など）への追従
 *
 * 開閉アニメーション本体のCSSは _accordion.scss の
 * `details::details-content` / `@supports (interpolate-size: allow-keywords)` を参照。
 */

/** アコーディオンの外枠。既存の .js-accordion と、新規用の [data-accordion] の両方を受け付ける */
const ACCORDION_SELECTOR = ".js-accordion, [data-accordion]";

/** 開閉する中身に付けるクラス（既存ページと共通） */
const CONTENT_CLASS = "detail-content";

/** 高さ実測中だけ付与するクラス。閉じた状態でも中身の高さを取れるようにする（_accordion.scss 参照） */
const MEASURING_CLASS = "is-measuring";

/**
 * <details> の中身要素を解決する
 * .detail-content が無ければ summary の次要素を中身とみなして付与する（自由入力HTML対応）
 *
 * @param {HTMLDetailsElement} details
 * @param {HTMLElement|null} summary
 * @return {HTMLElement|null}
 */
const resolveContent = (details, summary) => {
  const existing = details.querySelector(`:scope > .${CONTENT_CLASS}`);
  if (existing) return existing;

  // 中身が複数要素に分かれている場合は、最初の要素だけがアニメーション対象になる。
  // 自由入力コンテンツは 1 要素（例: <div class="rich-text">）にまとめること。
  const next = summary?.nextElementSibling ?? null;
  if (!next) return null;

  next.classList.add(CONTENT_CLASS);
  return next;
};

/**
 * 中身の高さを取得する
 * 閉じている間はブラウザがレイアウトをスキップして 0 を返すことがあるため、
 * その場合だけ一時的に測定用クラスを当てて実測する
 *
 * @param {HTMLElement} accordion
 * @param {HTMLElement} content
 * @return {Number}
 */
const readContentHeight = (accordion, content) => {
  const height = content.scrollHeight;
  if (height > 0) return Math.ceil(height);

  accordion.classList.add(MEASURING_CLASS);
  const measured = Math.ceil(content.scrollHeight);
  accordion.classList.remove(MEASURING_CLASS);

  return measured;
};

/**
 * --details-height を書き込む
 * 外枠と <details> の両方に入れることで、既存ページの2系統の書き込み先を吸収する
 */
const applyHeight = (accordion, details, content) => {
  const height = readContentHeight(accordion, content);
  if (!height) return;

  accordion.style.setProperty("--details-height", `${height}px`);
  details.style.setProperty("--details-height", `${height}px`);
};

/**
 * アコーディオンを初期化する（冪等）
 * root を渡せるので、モーダルなど後から挿入されたDOMにも適用できる
 *
 * @param {Document|HTMLElement} root
 * @return {void}
 */
export const initAccordions = (root = document) => {
  const accordions = root.querySelectorAll?.(ACCORDION_SELECTOR);
  if (!accordions?.length) return;

  accordions.forEach((accordion) => {
    if (accordion.dataset.accordionReady === "true") return;

    const details = accordion.matches("details") ? accordion : accordion.querySelector("details");
    if (!details) return;

    const summary = details.querySelector(":scope > summary");
    const content = resolveContent(details, summary);
    if (!summary || !content) return;

    // aria の紐付け
    if (!content.id) content.id = uid("accordion-content");
    summary.setAttribute("aria-controls", content.id);
    summary.setAttribute("aria-expanded", String(details.open));

    details.addEventListener("toggle", () => {
      summary.setAttribute("aria-expanded", String(details.open));
      applyHeight(accordion, details, content);
    });

    // 画像読み込み・フォント適用・モーダル表示などで中身の高さが変わったら再計測
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        if (accordion.classList.contains(MEASURING_CLASS)) return;
        applyHeight(accordion, details, content);
      });
      observer.observe(content);
    }

    applyHeight(accordion, details, content);
    accordion.dataset.accordionReady = "true";
  });
};

/**
 * 既存ページ（index / news）向け：.js-accordion の高さを実測して --details-height に入れる
 * app.js の syncFaqAnswerHeight から委譲される。挙動は従来と同一。
 *
 * @param {Document|HTMLElement} root
 * @return {void}
 */
export const syncAccordionHeights = (root = document) => {
  const accordions = root.querySelectorAll?.(".js-accordion");
  if (!accordions?.length) return;

  accordions.forEach((item) => {
    const detail = item.querySelector(`.${CONTENT_CLASS}`);
    if (!detail) return;
    item.style.setProperty("--details-height", `${Math.ceil(detail.scrollHeight)}px`);
  });
};

/**
 * 既存ページ（news）向け：.cms-content 内の summary にトグルアイコンを注入する
 * app.js の appendFaqToggleToSummaries から委譲される。挙動は従来と同一。
 *
 * @param {Document|HTMLElement} root
 * @return {void}
 */
export const enhanceCmsAccordions = (root = document) => {
  const summaries = root.querySelectorAll?.(".cms-content summary");
  if (!summaries?.length) return;

  summaries.forEach((summary) => {
    if (!summary.querySelector(".faq-item__toggle")) {
      const toggle = document.createElement("span");
      toggle.className = "faq-item__toggle";
      toggle.setAttribute("aria-hidden", "true");
      summary.appendChild(toggle);
    }

    const detailContent = summary.nextElementSibling;

    if (detailContent && detailContent.tagName === "DIV") {
      detailContent.classList.add(CONTENT_CLASS);
      detailContent.parentElement.style.setProperty("--details-height", `${Math.ceil(detailContent.scrollHeight)}px`);
    }
  });
};
